/// <amd-module name="RMWidgets/ReleasePipelineWidget/components/EnvironmentBox" />
import * as React from "react";

import { VssComponent } from "VSS/Platform/Layout";
import { format } from "VSS/Core/Util/String";
import { Tooltip } from "VSSUI/TooltipEx";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Status, StatusSize } from "VSSUI/Status";
import { Location } from "VSSUI/Utilities/Position";

import { ReleaseEnvironment, EnvironmentStatus } from "Release/Client/Contracts/Release";
import { getIconStatusProps, getBorderColorCssPrefix } from "Release/EnvironmentNode/Helpers";

import { getEnvironmentStatusClassName, getEnvironmentStatusText } from "RMWidgets/ReleasePipelineWidget/ReleasePipelineStatusUtils";
import * as Resources from "RMWidgets/ReleasePipelineWidget/Resources";


export interface EnvironmentBoxProps {
    // Metadata for the release environment.
    releaseEnvironment?: ReleaseEnvironment;

    // Name of the environment.
    environmentName: string;

    // Name of the current release on the environment.
    releaseName: string;

    // URL for the environment's summary page.
    summaryUrl: string;

    // URL for the current release.
    releaseUrl: string;

    // Opens links in new tab if true i.e. using ctrl + click or middle button.
    openLinksInNewTab?: boolean;
}

export class EnvironmentBox extends VssComponent<EnvironmentBoxProps> {

    constructor(props: EnvironmentBoxProps) {
        super(props);
    }

    render(): JSX.Element {
        const ariaLabelText = this.props.releaseEnvironment ?
            format(Resources.EnvironmentControlAriaLabelFormat, this.props.environmentName, getEnvironmentStatusText(this.props.releaseEnvironment.status), this.props.releaseName) :
            format("{0}; {1}", this.props.environmentName, Resources.NoDeploymentsYetMessage);

        return (
            <div
                className="rd-widget-env-container rd-widget-nav-cell"
                aria-label={ariaLabelText}
            >
                {this.renderEnvironmentNameContainer()}
                {this.renderReleaseInfoContainer()}
            </div>
        );
    }

    private renderEnvironmentNameContainer(): JSX.Element {
        const environmentStatus = this.props.releaseEnvironment ? this.props.releaseEnvironment.status : EnvironmentStatus.Undefined;
        let textClass = "summarylink rd-widget-envname";
        if (environmentStatus == EnvironmentStatus.PartiallySucceeded) {
            textClass = "partially-succeeded-text-color " + textClass;
        }
        return (
            <div className={getEnvironmentStatusClassName(environmentStatus)}>
                <Tooltip
                    anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                    overflowOnly={true}
                    text={this.props.environmentName}
                    tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
                >
                    <div className="rd-widget-status-color-strip">
                        <div className="rd-widget-envname-cont text-wrap">
                            <FPSLink
                                className={textClass}
                                href={this.props.summaryUrl}
                                target={this.props.openLinksInNewTab ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                tooltipProps={{
                                    text: this.props.environmentName,
                                    className: "rm-rich-content-tooltip"
                                }}>
                                {this.props.environmentName}
                            </FPSLink>
                        </div>
                    </div>
                </Tooltip>
            </div >
        );
    }

    private renderReleaseInfoContainer(): JSX.Element {
        if (!this.props.releaseEnvironment) {
            return <div className="text-wrap no-deployment-message">{Resources.NoDeploymentsYetMessage}</div>
        } else {
            const statusIndicator = getBorderColorCssPrefix(this.props.releaseEnvironment);
            const statusProps = getIconStatusProps(statusIndicator);

            return <div className="release-environment-info">
                <Status {...statusProps} className={"env-status-icon"} size={StatusSize.s} />
                <div className='release-name text-wrap'>
                    <FPSLink
                        href={this.props.releaseUrl}
                        target={this.props.openLinksInNewTab ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        tooltipProps={{
                            text: this.props.releaseName,
                            className: "rm-rich-content-tooltip"
                        }}>
                        {this.props.releaseName}
                    </FPSLink>
                </div>
            </div>
        }
    }
}

VssComponent.register("environmentBox", EnvironmentBox);
